import { z } from "zod"

import { ReceiptDetailField, ReceiptTemplateKey, type ReceiptConfig } from "@/features/finance/model/finance-types"

/** The receipt settings form (design R1): every setting a branch can change, saved together. */
export const receiptSettingsFormSchema = z.object({
  templateKey: z.nativeEnum(ReceiptTemplateKey),
  /** Null = never chosen, which prints every row (see `isDetailFieldEnabled`). */
  detailFields: z.array(z.nativeEnum(ReceiptDetailField)).nullable(),
  organizationNameOverride: z.string(),
  branchNameOverride: z.string(),
  headerSummary: z.string(),
  notesText: z.string(),
  footerText: z.string(),
  footerContacts: z.string(),
  signatureLabel: z.string(),
  signedByName: z.string(),
  signedByTitle: z.string(),
  signatureSvg: z.string().nullable(),
})

export type ReceiptSettingsFormValues = z.infer<typeof receiptSettingsFormSchema>

/** A loaded (normalized, prefilled) config to the form. */
export function toReceiptSettingsFormValues(config: ReceiptConfig): ReceiptSettingsFormValues {
  return {
    templateKey: config.templateKey,
    detailFields: config.detailFields ? [...config.detailFields] : null,
    organizationNameOverride: config.organizationNameOverride ?? "",
    branchNameOverride: config.branchNameOverride ?? "",
    headerSummary: config.headerSummary ?? "",
    notesText: config.notesText ?? "",
    footerText: config.footerText ?? "",
    footerContacts: config.footerContacts ?? "",
    signatureLabel: config.signatureLabel ?? "",
    signedByName: config.signedByName ?? "",
    signedByTitle: config.signedByTitle ?? "",
    signatureSvg: config.signatureSvg ?? null,
  }
}

/** The form to the full config the endpoint takes (it only accepts the whole object). */
export function toReceiptConfig(values: ReceiptSettingsFormValues): ReceiptConfig {
  return { ...values, detailFields: values.detailFields, accentColor: null }
}
